import { computed, onScopeDispose, watch } from 'vue'

import { useI18n } from 'vue-i18n'

import type { SearchContext } from '@common/shared-ui-layer/types/search-context'

import { DISCOVER_SEARCH_QUERY_KEY } from '../constants/discover.constants'
import { isDiscoverPath, readDiscoverQueryParam } from '../utils/discover-listing.utils'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

const DEBOUNCE_MS = 300

/**
 * Drives the Client Portal top bar search while the user is on `/discover`.
 *
 * Unlike the courses/communities contexts there is no dedicated search *page* — Discover filters
 * in place, so the query lives in the `?q=` param and `useDiscoverFilters` reads it back. That
 * keeps the results shareable/bookmarkable and survives a refresh without any store.
 */
export function useDiscoverSearchContext(route: RouteLocationNormalizedLoaded, router: Router): SearchContext {
  const { t } = useI18n()

  const isDiscoverPage = computed(() => isDiscoverPath(route.path?.toString()))

  const shouldShowSearch = computed(() => isDiscoverPage.value)

  const initialQuery = computed(() => (isDiscoverPage.value ? readDiscoverQueryParam(route.query[DISCOVER_SEARCH_QUERY_KEY]) : ''))

  /** `replace` (not `push`) so typing does not stack one history entry per keystroke. */
  const applyQuery = (value: string) => {
    // The context lives in the root shell, so a debounce can fire after the user has left Discover.
    if (!isDiscoverPage.value) return
    const trimmed = value?.trim() ?? ''
    const next = { ...route.query }

    if (trimmed) {
      next[DISCOVER_SEARCH_QUERY_KEY] = trimmed
    } else {
      delete next[DISCOVER_SEARCH_QUERY_KEY]
    }

    void router.replace({ query: next })
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const clearTimer = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  const onInputChange = (value: string) => {
    if (!shouldShowSearch.value) return
    clearTimer()
    debounceTimer = setTimeout(() => {
      applyQuery(value)
      debounceTimer = null
    }, DEBOUNCE_MS)
  }

  /** Enter bypasses the debounce so the result set updates immediately. */
  const onSubmit = (query: string) => {
    if (!shouldShowSearch.value) return
    clearTimer()
    applyQuery(query)
  }

  const onSearchClose = () => {
    clearTimer()
    applyQuery('')
  }

  // The context outlives a single component, so a pending timer must not fire after teardown
  // or once the route has moved off Discover.
  onScopeDispose(clearTimer)
  watch(isDiscoverPage, onDiscover => {
    if (!onDiscover) clearTimer()
  })

  return {
    shouldShowSearch,
    initialQuery,
    onSubmit,
    onInputChange,
    onSearchClose,
    placeholder: t('discover.searchPlaceholder'),
  }
}
